import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, RefreshCw } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ConfirmationScreen = ({ referenceId }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/vendor/status/${referenceId}`);
      setStatus(res.data);
    } catch (err) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [referenceId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} data-testid="screen-5-confirmation" className="max-w-xl mx-auto text-center">
      <div className="w-16 h-16 rounded-full bg-[#FDF2F5] flex items-center justify-center mx-auto mb-6">
        <CheckCircle2 className="text-[#D81B60]" size={32} />
      </div>
      <h2 className="font-extrabold text-3xl sm:text-4xl mb-2">You're Confirmed!</h2>
      <p className="text-gray-500 mb-8">
        Reference ID: <span className="font-mono text-[#D81B60]" data-testid="confirmation-reference-id">{referenceId}</span>
      </p>

      <div className="rounded-2xl border border-gray-200 p-8 mb-6 text-left">
        <p className="font-extrabold text-2xl text-center mb-1" data-testid="confirmation-status-text">
          {status?.approval_status || "Confirmed"}
        </p>
        <p className="text-sm text-gray-400 text-center mb-6">Payment: {status?.payment_status || "Paid"}</p>

        {(status?.stall_selections || []).length > 0 && (
          <div className="space-y-2 mb-4">
            {status.stall_selections.map((s) => (
              <div key={s.date} data-testid={`confirmation-stall-${s.date}`} className="flex items-center justify-between rounded-xl bg-[#FDFBF7] px-4 py-3 text-sm">
                <span className="font-semibold">{s.date}</span>
                <span className="text-gray-500">Stall #{s.stall_number}{s.electricity ? " · Electrical Connection" : ""}{s.extra_table ? " · Extra Table" : ""}</span>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-between items-center pt-4 border-t border-gray-100">
          <span className="text-sm text-gray-500">Total Paid</span>
          <span className="font-extrabold text-xl text-[#D81B60]" data-testid="confirmation-amount-paid">₹{status?.amount_due?.toLocaleString("en-IN")}</span>
        </div>
      </div>

      <p className="text-sm text-gray-500 mb-8">
        Your stall booking is confirmed — no further action needed. A confirmation has been sent to your email.
      </p>

      <div className="flex flex-wrap justify-center gap-4">
        <button onClick={fetchStatus} data-testid="refresh-status-button" className="rounded-full border border-gray-300 px-6 py-3 text-sm flex items-center gap-2">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
        <Link to="/" data-testid="confirmation-back-home-link" className="rounded-full bg-[#D81B60] text-white px-6 py-3 text-sm hover:bg-[#AD144B] transition-colors duration-300">
          Back to Home
        </Link>
      </div>
    </motion.div>
  );
};
